import React, { useEffect, useRef, useState } from 'react';
import { resolveAdSlot } from '@/config/adsense';

/**
 * Google AdSense Unit Component
 * Displays Google AdSense ads with configurable formats and responsive sizing
 */
const AdSenseUnit = ({ 
  adSlot, 
  adFormat = 'auto', 
  adLayout = 'display',
  className = '',
  style = {},
  fullWidthResponsive = true 
}) => {
  // Resolve ad slot name to actual AdSense ad unit ID
  const resolvedAdSlot = resolveAdSlot(adSlot);
  const adRef = useRef(null);
  const [adError, setAdError] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Safe environment variable access
  const isDevelopment = typeof process !== 'undefined' && 
                       process.env && 
                       process.env.NODE_ENV === 'development';

  // Check if AdSense is available and loaded
  const isAdSenseLoaded = () => {
    return typeof window !== 'undefined' && 
           window.adsbygoogle && 
           Array.isArray(window.adsbygoogle);
  };

  useEffect(() => {
    let mounted = true;
    let timeoutId = null;

    const initializeAd = () => {
      if (!mounted || isInitialized) return;
      
      try {
        if (isAdSenseLoaded()) {
          // Initialize AdSense ad
          (window.adsbygoogle = window.adsbygoogle || []).push({});
          setIsInitialized(true);
          console.log('AdSense unit initialized:', resolvedAdSlot, '(slot:', adSlot, ')');
        } else {
          // Retry after a short delay if AdSense script isn't loaded yet
          timeoutId = setTimeout(() => {
            if (mounted && !isInitialized) {
              initializeAd();
            }
          }, 1000);
        }
      } catch (error) {
        console.error('AdSense initialization error:', error);
        if (mounted) {
          setAdError(true);
        }
      }
    };

    // Always initialize AdSense (required for Google approval)
    // Google needs to see ad units on the site for verification
    // Small delay to ensure DOM is ready
    timeoutId = setTimeout(initializeAd, 100);

    // Cleanup function
    return () => {
      mounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [resolvedAdSlot, isInitialized]);

  // Suppress custom placeholder UI - let Google show their placeholder ads instead
  // This is required for Google's approval process
  const showTestAd = false; // Never show our custom placeholders

  // Show error fallback if AdSense failed to load
  // In production, hide ads that fail to load (suppress error messages)
  if (adError) {
    if (!isDevelopment || process.env.NODE_ENV === 'production') {
      return null;
    }
    
    // In development, show error for debugging
    const errorStyle = {
      backgroundColor: '#fef2f2',
      border: '1px solid #fecaca',
      color: '#dc2626',
      padding: '12px',
      borderRadius: '8px',
      fontSize: '12px',
      textAlign: 'center',
      minHeight: '60px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      ...style
    };

    return (
      <div className={`adsense-error ${className}`} style={errorStyle}>
        Ad failed to load
      </div>
    );
  }

  return (
    <div className={`adsense-container ${className}`} style={style}>
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client="ca-pub-5985195929723169"
        data-ad-slot={resolvedAdSlot}
        data-ad-format={adFormat}
        data-ad-layout={adLayout}
        data-full-width-responsive={fullWidthResponsive.toString()}
      />
    </div>
  );
};

export default AdSenseUnit;
